import type { Directive, DirectiveBinding } from 'vue';

import { usePermission } from '../composables/usePermission';

/**
 * v-permission 디렉티브 — 권한 미보유 시 DOM 에서 제거.
 *
 * docs/05-rbac.md §5.2.
 *
 * 사용 예:
 *   <el-button v-permission="'mes:work-order:approve'">승인</el-button>
 *   <el-button v-permission="['mes:work-order:approve', 'mes:work-order:update']">조정</el-button>
 *
 * 시맨틱:
 *   - string  : 단일 코드, hasPermission(code)
 *   - string[]: 모두 보유해야 표시 (AND, hasAllPermissions(...codes))
 *   - 빈 값/undefined : 항상 표시 (no-op)
 *
 * 권한이 없으면 첫 mount 시 DOM 트리에서 제거하고, 이후 변경 시에는 display:none
 * 으로 숨긴다 (한번 제거한 노드를 다시 살리려면 v-if 가 필요하므로 update 시점은
 * 보수적으로 hide 처리).
 */
type Binding = DirectiveBinding<string | string[] | undefined>;

function evaluate(binding: Binding): boolean {
  const v = binding.value;
  if (v == null || v === '') return true;
  const { hasPermission, hasAllPermissions } = usePermission();
  if (typeof v === 'string') return hasPermission(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return true;
    return hasAllPermissions(...v);
  }
  return true;
}

export const vPermission: Directive<HTMLElement, string | string[] | undefined> = {
  mounted(el, binding) {
    if (!evaluate(binding)) {
      el.parentElement?.removeChild(el);
    }
  },
  updated(el, binding) {
    if (!evaluate(binding)) {
      el.style.display = 'none';
    } else {
      el.style.removeProperty('display');
    }
  },
};
